/**
 * Textos narrativos em português para o relatório PDF/impressão das estatísticas.
 * Alinhado ao contrato em docs/STATS_FRONTEND.md
 */

function formatTargetGroup(raw) {
  const s = String(raw || "").toLowerCase();
  if (s === "graduate" || s === "alumni") return "graduados";
  if (s === "employer") return "empregadores";
  return raw ? String(raw) : "não especificado";
}

/**
 * Introdução global do relatório (1–2 parágrafos).
 * @param {object} meta - statsData.meta
 * @param {{ generatedAtLabel?: string, targetGroupLabel?: string }} opts
 * @returns {string[]}
 */
export function buildGlobalIntroParagraphs(meta, opts = {}) {
  const title = meta?.form_title || "Inquérito";
  const code = meta?.form_code ? ` (código ${meta.form_code})` : "";
  const n = meta?.total_responses ?? 0;
  const when = opts.generatedAtLabel || "—";
  const group = opts.targetGroupLabel || formatTargetGroup(meta?.target_group);

  return [
    `Este relatório apresenta, de forma sintética, a distribuição das respostas ao inquérito "${title}"${code}, com base em ${n} submissão(ões) registada(s). Os dados foram compilados em ${when}, destinando-se ao acompanhamento institucional do sistema de monitoria.`,
    `O público-alvo considerado neste inquérito corresponde a: ${group}. As percentagens indicadas por pergunta respeitam as regras definidas pelo sistema de estatísticas (incluindo, quando aplicável, percentagens sobre o universo de respondentes e não sobre o total de opções seleccionadas).`,
  ];
}

function topOptionByCount(options) {
  if (!Array.isArray(options) || !options.length) return null;
  return [...options].sort((a, b) => (b.count ?? 0) - (a.count ?? 0))[0];
}

/**
 * Parágrafo narrativo para um bloco de pergunta simples (com question_id).
 */
export function buildQuestionNarrative(block) {
  const chartType = block?.chart_type;
  const data = block?.data || {};
  const stats = block?.stats || {};
  const label = block?.label || "Pergunta";
  const answered = stats.total_answered ?? 0;
  const skipped = stats.skipped ?? 0;

  const base = `Na pergunta "${label}", ${answered} respondente(s) forneceram resposta e ${skipped} ignoraram ou deixaram em branco.`;

  if (chartType === "pie_or_bar" || chartType === "horizontal_bar") {
    const options = Array.isArray(data.options) ? data.options : [];
    if (!options.length) {
      return `${base} Não há opções agregadas para esta pergunta.`;
    }
    const top = topOptionByCount(options);
    const topLine = top
      ? ` A opção mais frequente é "${top.label}", com ${top.count ?? 0} resposta(s)${
          typeof top.percentage === "number" ? ` (${top.percentage}% dos respondentes)` : ""
        }.`
      : "";
    if (chartType === "horizontal_bar") {
      return `${base}${topLine} Nota: nas perguntas de escolha múltipla, as percentagens são calculadas sobre o número de respondentes; a soma das percentagens pode exceder 100%.`;
    }
    return `${base}${topLine}`;
  }

  if (chartType === "histogram") {
    const dist = Array.isArray(data.distribution) ? data.distribution : [];
    const parts = [base];
    if (typeof stats.mean === "number") parts.push(` A média observada é ${stats.mean}.`);
    if (typeof stats.median === "number") parts.push(` A mediana é ${stats.median}.`);
    if (typeof stats.min === "number" && typeof stats.max === "number") {
      parts.push(` Os valores variam entre ${stats.min} e ${stats.max}.`);
    }
    if (dist.length) {
      const topBin = [...dist].sort((a, b) => (b.count ?? 0) - (a.count ?? 0))[0];
      if (topBin?.label != null) {
        parts.push(` O intervalo com maior concentração de respostas é "${topBin.label}" (${topBin.count ?? 0} caso(s)).`);
      }
    }
    return parts.join("");
  }

  if (chartType === "word_cloud_or_list") {
    const samples = Array.isArray(data.sample_responses) ? data.sample_responses : [];
    const sampleNote =
      samples.length > 0
        ? ` Seguem-se algumas respostas exemplificativas que ilustram o teor das contribuições recebidas.`
        : ` Não foram disponibilizadas respostas de exemplo neste relatório.`;
    return `${base}${sampleNote}`;
  }

  if (chartType === "unsupported" || !chartType) {
    return `${base} O tipo de visualização desta pergunta não está mapeado para síntese automática; consulte os dados brutos no sistema, se disponíveis.`;
  }

  return base;
}

/**
 * Texto introdutório para um grupo (super-question) e, se aplicável, sobre o radar agregado.
 */
export function buildGroupIntroNarrative(block) {
  const title = block?.label || "Grupo de perguntas";
  const agg = block?.aggregated || {};
  const nested = Array.isArray(block?.questions) ? block.questions : [];

  let text = `Este bloco agrupa ${nested.length} pergunta(s) sob o tema "${title}", permitindo uma leitura conjunta das dimensões avaliadas.`;

  if (agg.available && Array.isArray(agg.series) && agg.series.length) {
    const names = agg.series.map((s) => s.label || "Dimensão").filter(Boolean);
    text += ` O gráfico de radar resume, por dimensão (${names.join(", ")}), a intensidade relativa das respostas nas categorias indicadas nos eixos, facilitando a comparação visual entre áreas.`;
  } else {
    text += ` Não foi possível apresentar o resumo agregado (radar); seguem as perguntas individuais com os respectivos gráficos ou tabelas.`;
  }

  return text;
}
